import { useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { telemetryAjax } from "../../ajax/TelemetryAjax";
import { ErrorResponse } from "../../DTOs/Common/ErrorResponse";
import { PlantPositionStatusDTO } from "../../DTOs/Telemetry/PlantPositionStatusDTO";
import { useSignalRContext } from "../Contexts/SignalRContext";
import { useDashboardCard } from "../Contexts/DashboardCardContext";
import { useAjaxErrorToast } from "../Hooks/useAjaxErrorToast";
import { bearingBetween, unwrapHeading } from "./EquipmentMarker";

const MACHINE_POSITIONS_QUERY_KEY = ["telemetry", "positions", "current"];

export const RESYNC_INTERVAL_MS = 60_000;

export type MachinePosition = {
  plant: PlantPositionStatusDTO;
  heading: number;
  headingKnown: boolean;
};

const NO_POSITIONS: MachinePosition[] = [];

function isOlder(candidate: PlantPositionStatusDTO, held: PlantPositionStatusDTO): boolean {
  return candidate.timestampUtc.toMillis() < held.timestampUtc.toMillis();
}

function toPosition(plant: PlantPositionStatusDTO, previous?: MachinePosition): MachinePosition {
  const reported = plant.headingDegrees
    ?? (previous
      ? bearingBetween(
        { lat: previous.plant.latitude, lng: previous.plant.longitude },
        { lat: plant.latitude, lng: plant.longitude })
      : null);

  return {
    plant,
    heading: reported == null
      ? previous?.heading ?? 0
      : unwrapHeading(previous?.heading ?? null, reported),
    headingKnown: reported != null || (previous?.headingKnown ?? false),
  };
}

function mergeSnapshot(held: MachinePosition[], snapshot: PlantPositionStatusDTO[]): MachinePosition[] {
  const heldById = new Map(held.map(p => [p.plant.plantId, p]));

  return snapshot.map(row => {
    const current = heldById.get(row.plantId);
    return current && isOlder(row, current.plant) ? current : toPosition(row, current);
  });
}

function applyUpdate(held: MachinePosition[], updated: PlantPositionStatusDTO): MachinePosition[] {
  const index = held.findIndex(p => p.plant.plantId === updated.plantId);
  if (index === -1) return [...held, toPosition(updated)];
  if (isOlder(updated, held[index].plant)) return held;

  const next = [...held];
  next[index] = toPosition(updated, held[index]);
  return next;
}

export function useMachinePositions(): { positions: MachinePosition[]; isLoading: boolean; loadFailed: boolean } {
  const queryClient = useQueryClient();
  const { connection, isReconnecting } = useSignalRContext();
  const { onScreen } = useDashboardCard();

  const { data: positions = NO_POSITIONS, isLoading, isError, failureCount, error, refetch } = useQuery({
    queryKey: MACHINE_POSITIONS_QUERY_KEY,
    queryFn: async () => {
      const res = await telemetryAjax.getCurrentPositions();
      if (ErrorResponse.isErrorResponse(res)) throw res;
      return mergeSnapshot(queryClient.getQueryData<MachinePosition[]>(MACHINE_POSITIONS_QUERY_KEY) ?? [], res);
    },
    staleTime: 0,
    refetchInterval: RESYNC_INTERVAL_MS,
    subscribed: onScreen,
  });

  useAjaxErrorToast(error, "Failed to load machine positions.");

  useEffect(() => {
    if (!connection) return;

    const handleUpdate = (updated: PlantPositionStatusDTO) => {
      queryClient.setQueryData<MachinePosition[]>(MACHINE_POSITIONS_QUERY_KEY, held => held && applyUpdate(held, updated));
    };

    connection.on("PlantPositionStatusUpdatedAsync", handleUpdate);
    return () => connection.off("PlantPositionStatusUpdatedAsync", handleUpdate);
  }, [connection, queryClient]);

  const isHubLive = !!connection && !isReconnecting;
  const hubWasLive = useRef<boolean | null>(null);

  useEffect(() => {
    const wasLive = hubWasLive.current;
    hubWasLive.current = isHubLive;
    if (!connection || !isHubLive) return;

    const watch = () => connection
      .invoke("WatchMachinePositionsAsync")
      .catch(err => console.warn("WatchMachinePositionsAsync refused", err));

    watch().then(() => {
      if (wasLive === false) refetch();
    });

    const rewatch = () => {
      watch();
    };
    connection.on("UserInfoChangedAsync", rewatch);

    return () => {
      connection.off("UserInfoChangedAsync", rewatch);
      connection.invoke("UnwatchMachinePositionsAsync").catch(() => undefined);
    };
  }, [connection, isHubLive, refetch]);

  return { positions, isLoading, loadFailed: isError || failureCount > 0 };
}
